import React from "react";
import { Mail } from "lucide-react";
import SectionHeading from "@/components/site/SectionHeading";
import { BRAND } from "@/lib/site";

const SECTIONS = [
  {
    title: "1. Objetivo",
    body:
      "Este Código de Ética estabelece os princípios e padrões de conduta que orientam a atuação da Wellpec e de todos os seus colaboradores, sócios, prestadores de serviço e parceiros comerciais, reforçando o compromisso da empresa com a integridade, a transparência e a responsabilidade em todas as suas relações.",
  },
  {
    title: "2. Abrangência",
    body:
      "As diretrizes deste Código aplicam-se a todos os colaboradores da Wellpec, independentemente do cargo ou função, bem como a fornecedores, prestadores de serviço e demais parceiros que atuem em nome da empresa ou em suas dependências.",
  },
  {
    title: "3. Princípios e valores",
    body:
      "A atuação da Wellpec é orientada pelos seguintes princípios: integridade e honestidade em todas as relações comerciais; respeito às pessoas, à diversidade e aos direitos humanos; transparência na comunicação com clientes, fornecedores e colaboradores; e legalidade, com o cumprimento rigoroso da legislação aplicável em todas as jurisdições onde a empresa atua.",
  },
  {
    title: "4. Conduta profissional",
    body:
      "Espera-se que todos ajam com profissionalismo, cortesia e respeito mútuo, evitando qualquer forma de discriminação, assédio ou tratamento desrespeitoso. As decisões de negócio devem ser pautadas por critérios técnicos e comerciais objetivos, sem favorecimentos pessoais.",
  },
  {
    title: "5. Conflito de interesses",
    body:
      "Colaboradores e parceiros devem evitar situações em que interesses pessoais, financeiros ou familiares possam comprometer sua imparcialidade nas decisões relacionadas à Wellpec. Situações de potencial conflito devem ser comunicadas previamente à liderança responsável.",
  },
  {
    title: "6. Relacionamento com clientes e fornecedores",
    body:
      "A Wellpec preza por relações comerciais justas, baseadas em informações precisas sobre produtos, prazos e condições comerciais. A seleção de fornecedores segue critérios técnicos, de qualidade, prazo e conformidade documental, sem privilégios indevidos.",
  },
  {
    title: "7. Saúde, segurança e meio ambiente",
    body:
      "A empresa está comprometida com práticas que preservem a saúde e a segurança de colaboradores, clientes e comunidades, bem como com o uso responsável dos recursos naturais e o cumprimento da legislação ambiental aplicável às operações industriais que atende.",
  },
  {
    title: "8. Combate à corrupção e práticas antiéticas",
    body:
      "É vedada a oferta, promessa, solicitação ou aceitação de qualquer vantagem indevida — financeira ou não — a agentes públicos ou privados, com o objetivo de obter benefício comercial. A Wellpec repudia qualquer prática de suborno, fraude ou lavagem de dinheiro.",
  },
  {
    title: "9. Confidencialidade e proteção de dados",
    body:
      "Informações confidenciais de clientes, fornecedores e da própria empresa devem ser tratadas com sigilo e utilizadas exclusivamente para os fins a que se destinam, em conformidade com a legislação de proteção de dados vigente.",
  },
  {
    title: "10. Canal de ética",
    body:
      `Dúvidas, sugestões ou relatos relacionados a este Código podem ser encaminhados ao canal de contato da Wellpec, em ${BRAND.email}. Todos os relatos serão tratados com a devida atenção e confidencialidade.`,
  },
  {
    title: "11. Consequências do descumprimento",
    body:
      "O descumprimento das diretrizes deste Código sujeita colaboradores e parceiros às medidas cabíveis, incluindo sanções administrativas, contratuais e, quando aplicável, legais.",
  },
  {
    title: "12. Vigência",
    body:
      "Este Código de Ética entra em vigor na data de sua publicação e poderá ser revisado periodicamente pela Wellpec para refletir atualizações legais, regulatórias ou de gestão.",
  },
];

export default function CodigoDeEtica() {
  return (
    <>
      <section className="bg-white border-b border-surface-line">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-24">
          <div className="text-xs font-bold uppercase tracking-[0.22em] text-bronze">Governança</div>
          <h1 className="mt-4 max-w-4xl font-display text-4xl font-black leading-[1.05] tracking-tight text-ink sm:text-5xl lg:text-6xl">
            Código de <span className="text-brand">Ética</span>.
          </h1>
          <p className="mt-6 max-w-3xl text-lg leading-relaxed text-ink-muted">
            Os princípios que orientam a conduta da Wellpec com colaboradores, clientes, fornecedores e parceiros comerciais.
          </p>
        </div>
      </section>

      <section className="bg-surface-alt py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <SectionHeading eyebrow="Documento institucional" title="Princípios e diretrizes de conduta." />
          <div className="mt-12 space-y-10">
            {SECTIONS.map((s) => (
              <div key={s.title} className="rounded-lg border border-surface-line bg-white p-8 shadow-sm">
                <h2 className="font-display text-xl font-bold text-ink">{s.title}</h2>
                <p className="mt-3 text-sm leading-relaxed text-ink-muted">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-brand-noise py-20 text-white">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h2 className="font-display text-3xl font-bold sm:text-4xl">Alguma dúvida sobre nossa conduta?</h2>
          <p className="mx-auto mt-4 max-w-2xl text-white/80">Fale diretamente com nosso time.</p>
          <a href={`mailto:${BRAND.email}`} className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-7 py-4 font-semibold text-brand">
            Enviar e-mail <Mail className="h-4 w-4" />
          </a>
        </div>
      </section>
    </>
  );
}
